// 409 conflict resolution dialog: keeps both versions visible side by side.
import { api } from './api.js';
import { openModal } from './modals.js';
import { formatTime, h } from './dom.js';

export type ConflictChoice = 'theirs' | 'mine' | null;

const SNIPPET_LINES = 24;

function snippet(text: string): string {
  const lines = text.split(/\r?\n/);
  const head = lines.slice(0, SNIPPET_LINES).join('\n');
  return lines.length > SNIPPET_LINES ? `${head}\n… (${lines.length - SNIPPET_LINES} more lines)` : head;
}

/**
 * Shows the concurrent-edit dialog. Returns `theirs` (reload from the server),
 * `mine` (force overwrite with `baseHash: null`) or `null` (decide later).
 */
export async function showConflictDialog(path: string, mine: string): Promise<ConflictChoice> {
  const { promise, resolve } = Promise.withResolvers<ConflictChoice>();
  let settled = false;
  const finish = (choice: ConflictChoice): void => {
    if (settled) return;
    settled = true;
    resolve(choice);
  };
  const modal = openModal({ title: `Conflict on ${path}`, wide: true, onClose: () => finish(null) });
  const theirsPane = h('pre', { class: 'conflict-text', text: 'Loading server version…' });

  modal.body.append(
    h('p', {
      class: 'modal-text',
      text: 'This note changed on the server since you started editing. Choose which version to keep.',
    }),
    h('div', { class: 'conflict-grid' }, [
      h('div', { class: 'conflict-col' }, [h('h4', { text: 'Mine (editing right now)' }), h('pre', { class: 'conflict-text', text: snippet(mine) })]),
      h('div', { class: 'conflict-col' }, [h('h4', { class: 'conflict-title' }), theirsPane]),
    ]),
    h('div', { class: 'modal-actions' }, [
      h('button', {
        class: 'ghost-button',
        text: 'Decide later',
        type: 'button',
        on: { click: () => modal.close() },
      }),
      h('button', {
        class: 'ghost-button',
        text: 'Keep theirs (reload)',
        type: 'button',
        on: {
          click: () => {
            finish('theirs');
            modal.close();
          },
        },
      }),
      h('button', {
        class: 'primary-button danger',
        text: 'Keep mine (overwrite)',
        type: 'button',
        on: {
          click: () => {
            finish('mine');
            modal.close();
          },
        },
      }),
    ]),
  );

  try {
    const server = await api.readFile(path);
    theirsPane.textContent = snippet(server.content);
    const title = modal.body.querySelector('.conflict-title');
    if (title) title.textContent = `Theirs (server · modified ${formatTime(server.mtime)})`;
  } catch (error) {
    theirsPane.textContent = `Server version unavailable: ${(error as Error).message}`;
  }
  return promise;
}
